/**
 * Data contracts + providers for the v2 homepage (Homepage A public /
 * Homepage B "My Market"), per docs/my-market/platex-phase0-specs.md.
 *
 * Every provider here is explicitly one of three kinds and must stay
 * honestly labelled until replaced:
 *
 *  - LIVE:        backed by an existing endpoint (called by components via
 *                 the existing services; this file only shapes the data).
 *  - STATIC:      real, sourced figures that have no endpoint yet (index
 *                 deliverables, editorial picks). Provenance noted inline.
 *  - PLACEHOLDER: marketing copy the product wants but no system can back
 *                 yet. Kept in ONE constant so nothing fake leaks into
 *                 templates piecemeal. Remove as endpoints land.
 */

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export interface IndexSnapshot {
  value: string;
  yoyLabel: string;
  /** Direction of the change, drives the arrow colour ('up' | 'down' | 'flat'). */
  direction: 'up' | 'down' | 'flat';
  /** One-line context, e.g. distance from the 2015 base. */
  contextLabel: string;
  baseLabel: string;
  sourceLabel: string;
  asOf: string;
}

export interface EditorialFeature {
  eyebrow: string;
  title: string;
  standfirst: string;
  ctaLabel: string;
  targetUrl: string;
  /**
   * Full-bleed feature photography (production PlateX banner asset). The
   * opening emotional statement / tone before the personalised content;
   * also reused as the briefing-visual thumbnail on the member homepage.
   */
  imageUrl: string;
}

export interface CollectionCard {
  eyebrow: string;
  registration: string;
  copy: string;
  factLine: string;
  targetUrl: string;
}

// ---------------------------------------------------------------------------
// STATIC — real figures without an endpoint yet
// ---------------------------------------------------------------------------

/**
 * STATIC: PlateX Plate Price Index v2 (index 2026.10-v2, sold data to
 * 2026-10-21; platex-index index_computation/out/platex_index_values.csv,
 * series=headline, level=dvla). Mix-adjusted index of every plate sold at
 * auction (DVLA auctions and auction houses), like for like, base 2015 = 100.
 * Spec: platex-index/SITE_INDEX_SPEC.md.
 * The big number is the last 12 months (Q4 2025-Q3 2026, geometric mean of
 * the four quarters) against the same four quarters a year earlier, which
 * smooths the DVLA auction calendar. Changes quarterly with the January,
 * April, July and October releases. Replaced by a read of the published
 * release (s3 platex-index/latest/) or the `index.moved` producer (D5).
 */
export const STATIC_INDEX_SNAPSHOT: IndexSnapshot = {
  value: '159.5',
  yoyLabel: '0.7% on a year earlier',
  direction: 'up',
  contextLabel: '59.5% above its 2015 level',
  baseLabel: 'Rebased to 100 in 2015 · auction sales, like for like',
  sourceLabel: 'PlateX Plate Price Index',
  asOf: 'to Sep 2026',
};

/**
 * STATIC: the headline annual series behind the index tile (index 2026.10-v2,
 * series=headline, level=dvla, base 2015 = 100). Real modelled values, not
 * illustrative. 2026 is year to date (sold data to 21 Oct) and is drawn
 * dashed. Replaced by the published release when the index gets an endpoint.
 */
export interface IndexPoint {
  year: number;
  value: number;
  /** Year to date: drawn as a dashed final segment. */
  partial?: boolean;
}
export const STATIC_INDEX_SERIES: IndexPoint[] = [
  { year: 2015, value: 100.0 },
  { year: 2016, value: 108.2 },
  { year: 2017, value: 106.7 },
  { year: 2018, value: 112.6 },
  { year: 2019, value: 114.3 },
  { year: 2020, value: 140.6 },
  { year: 2021, value: 172.3 },
  { year: 2022, value: 161.8 },
  { year: 2023, value: 158.0 },
  { year: 2024, value: 155.5 },
  { year: 2025, value: 157.2 },
  { year: 2026, value: 160.1, partial: true },
];

/**
 * STATIC editorial: the In Focus feature. Curated by the editorial desk;
 * figures are real PlateX records. Swap by editing this constant until the
 * editorial feature endpoint exists.
 * Featured: 25 O, Britain's record registration. prod platex-pricing.soldplate
 * holds it as a DVLA auction sale (type 0), Nov 2014, all-in £513,680 (hammer
 * ~£400,000; widely reported as £518,480 incl. fees). Rounded to "£500,000"
 * in the headline. Banner image is a decorative PlateX asset, not of 25 O.
 */
export const STATIC_IN_FOCUS: EditorialFeature = {
  eyebrow: 'In Focus',
  title: 'The £152,700 plate, and a century of keepers.',
  standfirst:
    'When FU 2 came up at Bonhams’ Goodwood sale in 2016 it cleared ' +
    '£152,700, still bolted to a humble Mercedes hatchback. For four decades ' +
    'it had ridden on a primrose E-Type, a purple Rolls and a circus ' +
    'dynasty’s cars. We trace how a two-character plate became one of ' +
    'Britain’s most notorious marks.',
  ctaLabel: 'Read the story',
  targetUrl: '/blog/fu-2-a-two-character-provocation-with-a-century-of-keepers',
  imageUrl:
    'https://cdn.platex.co.uk/site-media/13985830-4ac9-4746-9205-f9b668763be9_fu2-rolls-moor-hero.jpg',
};

/**
 * STATIC editorial: the public Collections section (Homepage A). Curated;
 * sale figures are real PlateX records (design footnote: "Verified sale
 * figures are real · PlateX records").
 */
export const STATIC_COLLECTION_OF_WEEK = {
  eyebrow: 'Collection of the week',
  title: 'Number Ones · the blue-chip of the registration world',
  copy:
    'A single digit paired with initials: the rarest and most enduring ' +
    'format in the market, and the one collectors return to first. Values ' +
    'have held for decades.',
  ctaLabel: 'View the collection',
  targetUrl: '/number-1',
};

/**
 * STATIC owner picks (2026-11-03). Prices captured that day from the live
 * listings; dealer pages for 32 PY checked directly (SpeedyReg, RegHunters,
 * WesternReg, Dateless Registrations, AbsoluteReg all open on the plate):
 *   32 PY    cheapest dateless 2x1/2x2 listed, from £8,600 (SpeedyReg)
 *   CO27 ADO 27 new release, perfect reading CORTADO (27.json), from £1,299
 *   PLA 73S  reads PLATES, dealer listings from £15,150
 * Plate strings are pre-spaced because pla-plate renders rawPlate verbatim.
 * (Links use /plate-detail/<combo>; staging's /plates/<combo> route does not
 * exist on dev2.)
 */
export const STATIC_CURATOR_PICKS: CollectionCard[] = [
  {
    eyebrow: "Curator's pick",
    registration: '32 PY',
    copy: 'Two letters and two numbers, with no year to date it: the most affordable dateless 2×2 currently listed.',
    factLine: 'For sale · from £8,600',
    targetUrl: '/plates/32PY',
  },
  {
    eyebrow: 'New release',
    registration: 'CO27 ADO',
    copy: 'Reads CORTADO: one of the perfect readings in the 27 series.',
    factLine: 'New release · from £1,299',
    targetUrl: '/plates/CO27ADO',
  },
  {
    eyebrow: "Editor's selection",
    registration: 'PLA 73S',
    copy: 'Reads PLATES: the whole market on a single registration.',
    factLine: 'For sale · from £15,150',
    targetUrl: '/plates/PLA73S',
  },
];

// ---------------------------------------------------------------------------
// STATIC — hero trust strip (verified production counts)
// ---------------------------------------------------------------------------

export interface TrustStat {
  value: string;
  label: string;
}

/**
 * STATIC: hero trust strip, four headline platform figures. Each is a
 * rounded-DOWN band of a VERIFIED production count captured 2026-11-01, not a
 * live feed and not a guess. All re-verifiable from the read-only replica:
 *  - 80M+  registrations indexed. plato-logical DVLA universe. The live
 *          statcount cache reads ~77.3M (a pg_class.reltuples estimate, ~1%
 *          low); the search index holds 81,026,874. 80M+ is the honest band.
 *  - 39M+  sale prices on record. prod platex-pricing.soldplate = 39,435,501
 *          genuine sales (DVLA fixed price 39,088,962 + DVLA auction type 0+6
 *          339,297 + auction houses ~7,242; excludes withdrawals/price-changes).
 *  - 9M+   vehicle assignments mapped. prod platex-history-data.mot_reg_history
 *          cross-paired rows = 9,221,365 distinct plate-to-vehicle assignments.
 *  - 500K+ searches a month. prod plato-search API GET /search steady state
 *          ~16,000/day ~ 500,000/month (Oct 2026 baseline; bot-spike days out).
 * Replace with a public stats endpoint when one lands (Phase 2+ backend).
 */
export const HERO_TRUST_STATS: TrustStat[] = [
  { value: '500K+', label: 'Searches a month' },
  { value: '80M+', label: 'Registrations indexed' },
  { value: '39M+', label: 'Sale prices on record' },
  { value: '9M+', label: 'Vehicle assignments mapped' },
];

// ---------------------------------------------------------------------------
// The interim client-side briefing composer that previously lived here was
// REMOVED when the server composer shipped (GET /account/briefing +
// POST /account/briefing/seen, PlatexAPI feat/my-market-briefing). One
// composer only — see docs/my-market/PHASE3_BRIEFING_CONTRACT.md; the
// response contract types live in account-v2-service.ts.
// ---------------------------------------------------------------------------
